import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { useHelpdeskNavigation } from '../hooks/useHelpdeskNavigation';
import { z } from 'zod';
import { useCreateCustomer } from '../hooks/useCustomers';
import { useAuth } from '../hooks/useAuth';
import type { CreateCustomerRequest } from '../types/customers';
import type { Organization } from '../types/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { AlertCircle, Loader2, ArrowLeft, Save, Building2 } from 'lucide-react';
import { showToast } from '../lib/toast';
import { getErrorMessage } from '../lib/apiErrors';

const createCustomerSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50, 'First name is too long'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name is too long'),
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  phone: z.string().max(20, 'Phone number is too long').optional(),
});

type CreateCustomerForm = z.infer<typeof createCustomerSchema>;

export function CreateCustomer() {
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { availableOrganizations } = useAuth();
  const createCustomerMutation = useCreateCustomer();

  // Organization selection - default to first available org
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    availableOrganizations?.[0]?.id || ''
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
  } = useForm<CreateCustomerForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  });

  const onSubmit = async (rawData: Record<string, unknown>) => {
    try {
      clearErrors();

      // Validate organization selection
      if (!selectedOrgId) {
        setFormError('root', {
          type: 'manual',
          message: 'Please select an organization.'
        });
        return;
      }

      // Manually validate with Zod to prevent ZodError exceptions
      const validationResult = createCustomerSchema.safeParse(rawData);

      if (!validationResult.success) {
        // Handle validation errors manually without throwing
        validationResult.error.issues.forEach((err) => {
          const fieldName = err.path[0];
          if (fieldName) {
            setFormError(fieldName as keyof CreateCustomerForm, { type: 'manual', message: err.message });
          }
        });
        return; // Stop execution without throwing
      }

      const data = validationResult.data;

      // Create the customer
      const customerRequest: CreateCustomerRequest = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone?.trim() || undefined,
      };

      const customer = await createCustomerMutation.mutateAsync({
        customer: customerRequest,
        organizationId: selectedOrgId
      });

      showToast.success('Customer Created', `${customer.fullName} has been created successfully.`);

      // Navigate to the created customer's detail page
      navigateTo.customer(customer.id);
    } catch (err) {
      console.error('Error creating customer:', err);
      const message = getErrorMessage(err, 'Failed to create customer. Please try again.');
      setFormError('root', {
        type: 'manual',
        message
      });
      showToast.error('Creation Failed', message);
    }
  };

  return (
    <div className="container mx-auto py-4 sm:py-6 px-4 sm:px-6 max-w-3xl">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
          <Link to={getPath.customers()}>
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-foreground">Create New Customer</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Add a new customer to your help desk system</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <Card className="p-4 sm:p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
          {/* Root error display */}
          {errors.root && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>{errors.root.message}</span>
            </div>
          )}

          {/* Organization Selector - only show if user has multiple orgs */}
          {availableOrganizations && availableOrganizations.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="organization">
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization <span className="text-red-500">*</span>
                </span>
              </Label>
              <select
                id="organization"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="w-full h-10 px-3 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {availableOrganizations.map((org: Organization) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <p className="text-sm text-muted-foreground">
                Select which organization this customer belongs to
              </p>
            </div>
          )}

          {/* Name fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                {...register('firstName')}
                placeholder="John"
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                {...register('lastName')}
                placeholder="Doe"
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              placeholder="john.doe@example.com"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              {...register('phone')}
              placeholder="+1 (555) 123-4567"
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && (
              <p className="text-sm text-red-500">{errors.phone.message}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting || createCustomerMutation.isPending}
              className="flex-1 w-full"
            >
              {(isSubmitting || createCustomerMutation.isPending) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Customer
                </>
              )}
            </Button>
            <Link to={getPath.customers()} className="flex-1">
              <Button type="button" variant="outline" className="w-full">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}